/**
 * PageHeader — Reusable page title + optional action buttons
 */
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

interface Crumb {
  label: string;
  href?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: Crumb[];
  actions?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, breadcrumbs, actions }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: { xs: 'flex-start', sm: 'center' },
      justifyContent: 'space-between',
      flexDirection: { xs: 'column', sm: 'row' },
      gap: 2,
      mb: 3,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs sx={{ mb: 0.5 }} aria-label="breadcrumb">
          {breadcrumbs.map((crumb, i) =>
            crumb.href ? (
              <Link
                key={i}
                // Router link, not a bare href: an anchor here reloaded the
                // entire SPA — every bundle and every bootstrap request — and
                // threw away the query the list had been showing.
                component={RouterLink}
                to={crumb.href}
                underline="hover"
                color="text.secondary"
                sx={{ fontSize: '0.8125rem', fontWeight: 500 }}
              >
                {crumb.label}
              </Link>
            ) : (
              <Typography
                key={i}
                sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.primary' }}
              >
                {crumb.label}
              </Typography>
            )
          )}
        </Breadcrumbs>
      )}
      <Typography variant="h4" component="h1" sx={{ color: 'text.primary', lineHeight: 1.25 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {/* Actions may shrink and wrap rather than push the title into a 1-word
        column (or overflow the viewport) once a page carries many of them —
        `flexShrink: 0` with a non-wrapping child is what crushed the title. */}
    {actions && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          minWidth: 0,
          maxWidth: '100%',
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
        }}
      >
        {actions}
      </Box>
    )}
  </Box>
);

export default PageHeader;
